import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { color, font, studyStatusTheme } from '../styles/theme';
import { num, fullDate, shortDate } from '../lib/format';
import { SITE_URL } from '../lib/site';
import {
  useNewsletter,
  useSendNewsletter,
  useSendProgress,
  useRecipientCount,
  useLetterPreview,
  useSendTestLetter,
  useSubscribers,
  useUnsubscribe,
  type LetterDraft,
  type NewsletterAudience,
  type SubscriberStatus,
} from '../api/queries';
import { downloadCsv, type NewsletterResponse, type NewsletterPreview, type Subscriber } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  PageTitle,
  Card,
  CardTitle,
  Chip,
  Loading,
  ErrorNote,
  Button,
  Field,
  Meter,
  Confirm,
  Modal,
  RoundButton,
  StatusNote,
  inputStyle,
} from '../components/ui';
import { PreviewPill } from '../components/pills';
import { AboutPanel } from './MeetingBits';
import { audienceLabel, listTags, people, LETTER_NAME, type AudienceStudy } from '../lib/newsletterAudience';
import { LETTER_TEMPLATES, type LetterTemplate } from '../lib/newsletterTemplates';

/**
 * The newsletter: two lists from the public site, and a composer that asks
 * who a letter is for before anything is written.
 *
 * "A letter from the lab" (the Support page) promised occasional updates;
 * "Subscribe to this study" (a study page) promised that study's news and
 * nothing else. The audience choice sits above the subject because it
 * changes the wording, each choice shows its count, and the one irreversible
 * action — the send — repeats the audience and the number before it goes.
 *
 * A letter starts from a template, is written in plain text with a few marks
 * (the toolbar inserts them), and is previewed beside the composer exactly as
 * the API will send it — the branded layout, the footer, the reader's own
 * leave-the-list link. "Send me a test" mails one copy to the writer, since an
 * inbox draws an email differently from a browser.
 */

type Audiences = NewsletterResponse['audiences'];
type SentLetter = NewsletterResponse['sends'][number];

/** A template's "[Study name]" left in — the API refuses to send one (domain/newsletter.ts). */
const PROMPT = /\[[^\]\n]+\](?!\()/;

const AUDIENCE_NAME: Record<NewsletterAudience, string> = {
  letter: LETTER_NAME,
  studies: 'Subscribers of a study',
  everyone: 'Everyone',
};

/** A value that settles a moment after the last change — the preview waits for a pause in typing. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

export function Newsletter() {
  const q = useNewsletter();
  const send = useSendNewsletter();
  const test = useSendTestLetter();
  const { can } = useAuth();

  const [kind, setKind] = useState<NewsletterAudience>('letter');
  const [picked, setPicked] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [replacing, setReplacing] = useState<LetterTemplate | null>(null);
  const [viewing, setViewing] = useState<SentLetter | null>(null);
  const [sendId, setSendId] = useState<string | null>(null);
  const progress = useSendProgress(sendId);
  // Only ever asked for two or more studies: a union is not a sum.
  const union = useRecipientCount(kind === 'studies' ? picked : []);
  const qc = useQueryClient();
  const composer = useRef<HTMLDivElement>(null);

  const settledSubject = useSettled(subject, 450);
  const settledBody = useSettled(body, 450);
  const preview = useLetterPreview(
    settledBody.trim() ? { subject: settledSubject.trim() || 'Your subject line', body: settledBody } : null,
  );

  // When the polled send finishes, the history catches up with the progress
  // box, so "0 / 5 · queued" does not outlive the send.
  const finished = progress.data?.status === 'sent' || progress.data?.status === 'failed';
  useEffect(() => {
    if (finished) void qc.invalidateQueries({ queryKey: ['newsletter'] });
  }, [finished, qc]);

  const maySend = can('newsletter:send');

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote message={(q.error as Error).message} />;
  if (!q.data) return null;

  const n = q.data;
  const live = progress.data;
  // An API from before audiences (046) has no lists; the composer then sends
  // to everyone, as it always did, and says so with the Preview pill.
  const audiences = (n as { audiences?: Audiences }).audiences;
  const studies: AudienceStudy[] = audiences?.studies ?? [];
  const pickedStudies = studies.filter((s) => picked.includes(s.slug));

  /** How many the chosen audience reaches; null while the union is being counted. */
  const count: number | null = !audiences
    ? n.subscriberCount
    : kind === 'everyone'
      ? audiences.everyone
      : kind === 'letter'
        ? audiences.letter
        : picked.length === 0
          ? 0
          : picked.length === 1
            ? (pickedStudies[0]?.count ?? 0)
            : (union.data?.count ?? null);

  const label = audienceLabel(kind, picked, studies);
  const unfilled = PROMPT.exec(body)?.[0] ?? null;
  const reason =
    kind === 'studies' && picked.length === 0
      ? 'Tick at least one study.'
      : count === 0
        ? 'No one is on this list yet.'
        : unfilled
          ? `Fill in ${unfilled} before sending — it would reach every reader as it is.`
          : null;
  const hasDraft = !!subject.trim() && !!body.trim();
  const ready = !send.isPending && hasDraft && count !== null && count > 0 && !reason;

  async function submit() {
    setConfirming(false);
    try {
      const res = await send.mutateAsync(
        audiences
          ? { subject, body, audience: kind, ...(kind === 'studies' ? { studies: picked } : {}) }
          : { subject, body },
      );
      setSendId(res.id);
      setSubject('');
      setBody('');
      test.reset();
    } catch {
      /* surfaced through send.error */
    }
  }

  function applyTemplate(t: LetterTemplate) {
    setReplacing(null);
    setSubject(t.subject);
    setBody(t.body);
    test.reset();
  }
  const pickTemplate = (t: LetterTemplate) =>
    subject.trim() || body.trim() ? setReplacing(t) : applyTemplate(t);

  function reuse(s: SentLetter) {
    setViewing(null);
    setSubject(s.subject);
    setBody(s.body ?? '');
    test.reset();
    composer.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const toggle = (slug: string) =>
    setPicked(picked.includes(slug) ? picked.filter((s) => s !== slug) : [...picked, slug]);
  const tickable = studies.filter((s) => s.count > 0).map((s) => s.slug);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
      <PageTitle sub="Write a letter, see it as readers will, and send it to the lists from the public site. Kept separate from patient records.">
        Newsletter
      </PageTitle>

      <AboutPanel storageKey="enact.newsletter.about" title="How the lists and letters work">
        <p style={{ margin: 0 }}>
          <strong style={{ color: color.navy }}>{LETTER_NAME}</strong> — people who signed up on the public site’s{' '}
          <a href={`${SITE_URL}/newsletter`} target="_blank" rel="noreferrer">
            Support page
          </a>
          . They expect occasional updates on what is recruiting, what has published and what comes next.{' '}
          <strong style={{ color: color.navy }}>Study updates</strong> — people who pressed “Get notified when these
          open” on a{' '}
          <a href={`${SITE_URL}/research`} target="_blank" rel="noreferrer">
            study page
          </a>
          , promised email when that study opens or publishes, and nothing else.
        </p>
        <p style={{ margin: '6px 0 0' }}>
          Every letter goes out in the lab’s branded layout with the footer and the reader’s own “Leave the list”
          link — one link takes a person off every list. <code>{'{first name}'}</code> becomes each reader’s first
          name, or nothing when they gave none. Sends run in the background; progress and the history stay here.
        </p>
      </AboutPanel>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,470px),1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <div ref={composer} style={{ scrollMarginTop: 16 }}>
          <Card pad={26}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <CardTitle style={{ flex: 1 }}>Write a letter</CardTitle>
              {!audiences && <PreviewPill>Audience lists need the API update</PreviewPill>}
            </div>

            {maySend ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <Step n={1} title="Start from a template">
                  <TemplatePicker onPick={pickTemplate} />
                  <Confirm
                    open={replacing !== null}
                    title={`Start over from “${replacing?.name ?? ''}”?`}
                    detail="This replaces the subject and message you have written."
                    confirmLabel="Replace my draft"
                    onConfirm={() => replacing && applyTemplate(replacing)}
                    onCancel={() => setReplacing(null)}
                  >
                    <span />
                  </Confirm>
                </Step>

                {audiences && (
                  <Step n={2} title="Who gets it">
                    <div role="radiogroup" aria-label="Who gets it" style={{ display: 'grid', gap: 6 }}>
                      <AudienceChoice
                        on={kind === 'letter'}
                        onPick={() => setKind('letter')}
                        name={LETTER_NAME}
                        desc="Signed up on the Support page."
                        count={audiences.letter}
                      />
                      <AudienceChoice
                        on={kind === 'studies'}
                        onPick={() => setKind('studies')}
                        name="Subscribers of a study"
                        desc="Asked to hear when a study opens or publishes. Pick the studies below."
                        count={count}
                        countShown={kind === 'studies'}
                      >
                        {kind === 'studies' && (
                          <StudyPicker
                            studies={studies}
                            picked={picked}
                            onToggle={toggle}
                            onAll={() => setPicked(tickable)}
                            onClear={() => setPicked([])}
                            allTicked={tickable.length > 0 && tickable.every((s) => picked.includes(s))}
                          />
                        )}
                      </AudienceChoice>
                      <AudienceChoice
                        on={kind === 'everyone'}
                        onPick={() => setKind('everyone')}
                        name="Everyone"
                        desc="Both lists. Study subscribers were promised study news only, so keep this for lab-wide notices."
                        count={audiences.everyone}
                      />
                    </div>
                  </Step>
                )}

                <Step n={audiences ? 3 : 2} title="Write it">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Field label="Subject">
                      <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="e.g. A letter from the lab — October"
                        style={{ ...inputStyle, fontSize: 14.5, padding: '11px 14px' }}
                      />
                    </Field>
                    <LetterEditor value={body} onChange={setBody} />
                  </div>
                </Step>

                <Step n={audiences ? 4 : 3} title="Check and send">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* What pressing the button will do, with the number and the list named. */}
                    <div
                      style={{
                        background: reason && unfilled ? '#fbf5ec' : color.tint,
                        border: reason && unfilled ? '1px solid #efdcc2' : '1px solid transparent',
                        borderRadius: 10,
                        padding: '10px 14px',
                        fontSize: 13,
                        lineHeight: '20px',
                        color: reason && unfilled ? '#5a3d12' : color.ink,
                      }}
                    >
                      {reason ? (
                        <span>{reason}</span>
                      ) : count === null ? (
                        <span>Counting who this goes to…</span>
                      ) : (
                        <span>
                          <strong style={{ color: color.navy }}>When you send</strong>, this goes to{' '}
                          <strong style={{ color: color.navy }}>
                            {num(count)} {people(count)}
                          </strong>
                          : {label}.
                          {audiences && kind === 'studies' && picked.length > 1 && ' A person on two lists is counted once.'}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <Button
                        variant="ghost"
                        onClick={() => void test.mutateAsync({ subject, body }).catch(() => undefined)}
                        disabled={!hasDraft || test.isPending}
                        style={{ padding: '12px 20px', fontSize: 14 }}
                      >
                        {test.isPending ? 'Sending a test…' : 'Send me a test'}
                      </Button>
                      <Confirm
                        open={confirming}
                        title={`Send “${subject.trim()}”?`}
                        detail={`To ${num(count ?? 0)} ${people(count ?? 0)}: ${label}. A sent letter cannot be recalled.`}
                        confirmLabel="Send now"
                        onConfirm={() => void submit()}
                        onCancel={() => setConfirming(false)}
                      >
                        <Button
                          onClick={() => setConfirming(true)}
                          disabled={!ready}
                          style={{ padding: '13px 28px', fontSize: 14.5 }}
                        >
                          {send.isPending
                            ? 'Queueing…'
                            : !audiences
                              ? 'Send to all subscribers'
                              : count === null || reason
                                ? 'Send'
                                : `Send to ${num(count)} ${people(count)}`}
                        </Button>
                      </Confirm>
                    </div>

                    {test.data && (
                      <p style={{ margin: 0, fontSize: 12.5, color: test.data.sent ? color.green : '#7a6234', fontWeight: 700 }}>
                        {test.data.sent
                          ? `Test sent to ${test.data.to} — check that inbox (and its spam folder).`
                          : `Test written to the API’s log, not sent: no email provider is set where this dashboard’s API runs.`}
                      </p>
                    )}
                    {test.error && <ErrorNote message={(test.error as Error).message} />}

                    {!n.provider.configured && (
                      <span
                        title="Set EMAIL_PROVIDER on the API to send for real"
                        style={{
                          alignSelf: 'flex-start',
                          background: '#f4f0e6',
                          color: '#7a6234',
                          border: '1px solid #e8dcc4',
                          borderRadius: 999,
                          padding: '6px 13px',
                          fontSize: 11.5,
                          fontWeight: 800,
                        }}
                      >
                        No email provider — messages are logged, not sent
                      </span>
                    )}

                    {send.error && <ErrorNote message={(send.error as Error).message} />}

                    {live && (
                      <div style={{ background: color.tint, borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: color.blueDeep, flex: 1 }}>
                            “{live.subject}” · {live.status}
                          </span>
                          <span style={{ fontSize: 12.5, color: color.blueDeep, fontWeight: 700 }}>
                            {num(live.sentCount)} / {num(live.recipientCount)}
                          </span>
                        </div>
                        <p style={{ fontSize: 12, color: color.blueDeep, margin: '0 0 10px' }}>
                          To {audienceLabel(live.audience ?? 'everyone', live.audienceStudies ?? [], studies)}
                        </p>
                        <Meter
                          pct={live.recipientCount ? (live.sentCount / live.recipientCount) * 100 : 0}
                          track={color.white}
                        />
                        {live.failedCount > 0 && (
                          <p style={{ fontSize: 12, color: color.red, margin: '8px 0 0', fontWeight: 700 }}>
                            {live.failedCount} failed
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </Step>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: color.muted, margin: 0 }}>
                Your role can view the lists and past letters but not send to them.
              </p>
            )}
          </Card>
        </div>

        <div style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
          <LetterPreview
            preview={body.trim() ? preview.data : undefined}
            updating={preview.isFetching || body !== settledBody || subject !== settledSubject}
            error={preview.error as Error | null}
          />
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,320px),1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <div
          style={{
            background: color.blue,
            borderRadius: 20,
            padding: '22px 24px',
            color: color.white,
            boxShadow: '0 14px 30px rgba(51,98,142,0.28)',
          }}
        >
          <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 20px', opacity: 0.9 }}>Subscribers</p>
          <p
            style={{
              fontFamily: font.display,
              fontWeight: 600,
              fontSize: 32,
              lineHeight: 1,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            {num(n.subscriberCount)}
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                background: '#bfe6d2',
                color: '#14563c',
                padding: '3px 9px',
                borderRadius: 999,
              }}
            >
              ↑ {n.growth30d}
            </span>
          </p>
          <p style={{ fontSize: 11.5, opacity: 0.75, margin: '12px 0 0' }}>New in the last 30 days</p>
          {audiences && (
            <div
              style={{
                display: 'grid',
                gap: 7,
                borderTop: '1px solid rgba(255,255,255,0.22)',
                paddingTop: 12,
                marginTop: 14,
                fontSize: 12.5,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <StatRow name={LETTER_NAME} count={audiences.letter} />
              {studies.map((s) => (
                <StatRow key={s.slug} name={s.short} title={s.label} count={s.count} />
              ))}
            </div>
          )}
        </div>

        <Card pad="22px 24px">
          <CardTitle style={{ marginBottom: 8 }}>Sent letters</CardTitle>
          {n.sends.length === 0 ? (
            <p style={{ fontSize: 13, color: color.muted, margin: 0 }}>Nothing sent yet. Letters you send appear here.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {n.sends.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0,1fr) auto',
                    gap: '4px 12px',
                    alignItems: 'center',
                    padding: '11px 2px',
                    borderBottom: `1px solid ${color.line}`,
                    fontSize: 13,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        color: color.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {s.subject}
                    </div>
                    <div style={{ fontSize: 12, color: color.muted, marginTop: 2 }}>
                      To {audienceLabel(s.audience ?? 'everyone', s.audienceStudies ?? [], studies)}
                      {s.createdAt ? ` · ${shortDate(s.createdAt)}` : ''} · {num(s.sentCount)} / {num(s.recipientCount)}
                    </div>
                  </div>
                  <StatusChip status={s.status} />
                  {s.body ? (
                    <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 6 }}>
                      <SmallButton onClick={() => setViewing(s)}>View</SmallButton>
                      {maySend && <SmallButton onClick={() => reuse(s)}>Reuse as a new draft</SmallButton>}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {viewing && (
        <Modal
          label={`Letter: ${viewing.subject}`}
          header={
            <div>
              <div style={{ fontFamily: font.display, fontWeight: 600, fontSize: 17, color: color.navy }}>
                {viewing.subject}
              </div>
              <div style={{ fontSize: 12.5, color: color.muted, marginTop: 3 }}>
                To {audienceLabel(viewing.audience ?? 'everyone', viewing.audienceStudies ?? [], studies)}
                {viewing.createdAt ? ` · ${fullDate(viewing.createdAt)}` : ''} · {num(viewing.sentCount)} sent
              </div>
            </div>
          }
          footer={
            maySend ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={() => reuse(viewing)}>Reuse as a new draft</Button>
              </div>
            ) : undefined
          }
          onClose={() => setViewing(null)}
          width={720}
        >
          <SentLetterView letter={{ subject: viewing.subject, body: viewing.body ?? '' }} />
        </Modal>
      )}

      {/* Every subscriber, not just the newest: replaces the six-row "Recent
          sign-ups" glance, whose rows are this list's first page. */}
      <SubscriberList studies={studies} showLists={!!audiences} mayManage={maySend} />
    </div>
  );
}

const SUBSCRIBER_PAGE = 25;

/** The API's three statuses, in the words a person would use. */
const SUBSCRIBER_STATUSES: Array<{ key: SubscriberStatus; label: string }> = [
  { key: 'active', label: 'Subscribed' },
  { key: 'unsubscribed', label: 'Left the lists' },
  { key: 'all', label: 'Both' },
];

/**
 * Everyone who signed up, a page at a time, newest first (GET /subscribers/,
 * newsletter:read), searched by email address — the only thing the API
 * searches. For a role that may send (newsletter:send) each live row can be
 * taken off every list by hand, behind a Confirm, and what the list shows can
 * be exported as a CSV; both controls are absent for anyone else, because the
 * API would refuse them.
 */
function SubscriberList({
  studies,
  showLists,
  mayManage,
}: {
  studies: AudienceStudy[];
  /** The API has the two lists (046), so each row can say which it joined. */
  showLists: boolean;
  mayManage: boolean;
}) {
  const [typed, setTyped] = useState('');
  const q = useSettled(typed.trim(), 300);
  const [status, setStatus] = useState<SubscriberStatus>('active');
  // The page belongs to one search and one status: changing either starts again at page 1.
  const view = `${status}|${q}`;
  const [paging, setPaging] = useState({ view, page: 1 });
  const page = paging.view === view ? paging.page : 1;
  const list = useSubscribers({ q, status, page, pageSize: SUBSCRIBER_PAGE });
  const unsubscribe = useUnsubscribe();
  const [leaving, setLeaving] = useState<string | null>(null);
  /** Who was just taken off, said in place: in the Subscribed view the row itself goes. */
  const [removed, setRemoved] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const data = list.data;
  const rows = data?.subscribers ?? [];
  const total = data?.total ?? 0;
  const pageMax = data?.pageMax ?? 1;
  const shownPage = data?.page ?? page;

  async function exportCsv() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCsv(
        '/subscribers/export.csv',
        { q, status },
        `enact-subscribers-${new Date().toISOString().slice(0, 10)}.csv`,
      );
    } catch (err) {
      // The hourly limit (10 an hour) or a refused permission, in the API's words.
      setExportError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  function empty(): string {
    if (q) return `No address here matches “${q}”.`;
    if (status === 'unsubscribed') return 'No one has left the lists.';
    return 'No sign-ups yet. People who subscribe on the public site appear here.';
  }

  return (
    <Card pad="22px 24px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <CardTitle style={{ flex: 1 }}>All subscribers</CardTitle>
        {mayManage && (
          <Button
            variant="ghost"
            onClick={() => void exportCsv()}
            disabled={exporting}
            title="Download the people listed here, with the search and the choice below, as a spreadsheet"
            style={{ padding: '8px 16px', fontSize: 12.5 }}
          >
            {exporting ? 'Preparing…' : 'Export CSV'}
          </Button>
        )}
      </div>
      <p style={{ fontSize: 12.5, color: color.muted, margin: '0 0 14px' }}>
        Newest first. Someone who leaves stays on record, marked as left, and signing up on the site again puts them
        back.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          type="search"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Search by email address"
          aria-label="Search subscribers by email address"
          style={{ ...inputStyle, flex: '1 1 220px', minWidth: 0, padding: '8px 13px', fontSize: 13.5 }}
        />
        <div role="group" aria-label="Show" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {SUBSCRIBER_STATUSES.map((s) => (
            <Chip key={s.key} label={s.label} active={status === s.key} onClick={() => setStatus(s.key)} />
          ))}
        </div>
      </div>

      {exportError && (
        <div style={{ marginBottom: 12 }}>
          <ErrorNote message={exportError} />
        </div>
      )}
      {unsubscribe.error && (
        <div style={{ marginBottom: 12 }}>
          <ErrorNote message={(unsubscribe.error as Error).message} />
        </div>
      )}
      {removed && !unsubscribe.error && (
        <div style={{ marginBottom: 12 }}>
          <StatusNote>{removed} is off every list. They are under “Left the lists” now.</StatusNote>
        </div>
      )}

      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <ErrorNote message={(list.error as Error).message} />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              opacity: list.isPlaceholderData ? 0.6 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            {rows.map((s) => (
              <SubscriberRow
                key={s.id}
                sub={s}
                tags={showLists ? listTags(s, studies) : null}
                manage={
                  mayManage && s.active
                    ? {
                        confirming: leaving === s.id,
                        busy: unsubscribe.isPending && unsubscribe.variables === s.id,
                        onAsk: () => setLeaving(s.id),
                        onCancel: () => setLeaving(null),
                        onConfirm: () => {
                          setLeaving(null);
                          setRemoved(null);
                          unsubscribe.mutate(s.id, { onSuccess: () => setRemoved(s.email) });
                        },
                      }
                    : null
                }
              />
            ))}
            {rows.length === 0 && <p style={{ fontSize: 13, color: color.muted, margin: '6px 0 0' }}>{empty()}</p>}
          </div>

          {total > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: color.muted, flex: 1 }}>
                {num(total)} {people(total)}
                {pageMax > 1 && ` · page ${shownPage} of ${pageMax}`}
              </span>
              {pageMax > 1 && (
                <>
                  <RoundButton
                    title="Newer"
                    disabled={shownPage <= 1}
                    onClick={() => setPaging({ view, page: shownPage - 1 })}
                  >
                    ‹
                  </RoundButton>
                  <RoundButton
                    title="Older"
                    disabled={shownPage >= pageMax}
                    onClick={() => setPaging({ view, page: shownPage + 1 })}
                  >
                    ›
                  </RoundButton>
                </>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/** One subscriber: who, when, which lists — and, for a role that may send, taking them off by hand. */
function SubscriberRow({
  sub,
  tags,
  manage,
}: {
  sub: Subscriber;
  /** Null against an API without the two lists. */
  tags: string[] | null;
  manage: {
    confirming: boolean;
    busy: boolean;
    onAsk: () => void;
    onCancel: () => void;
    onConfirm: () => void;
  } | null;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) auto',
        gap: '6px 12px',
        alignItems: 'center',
        padding: '10px 2px',
        borderBottom: `1px solid ${color.line}`,
      }}
    >
      <span
        style={{
          fontSize: 13.5,
          color: sub.active ? color.ink : color.muted,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {/* The name is what a greeting line uses; older rows and the
            study-page box have none, so the address leads. */}
        {sub.firstName ? `${sub.firstName} · ${sub.email}` : sub.email}
      </span>
      <span style={{ fontSize: 12, color: color.muted, flex: 'none' }}>Joined {fullDate(sub.subscribedAt)}</span>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {tags?.map((t) => <ListTag key={t}>{t}</ListTag>)}
        {!sub.active && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: color.muted }}>
            Left the lists{sub.unsubscribedAt ? ` ${fullDate(sub.unsubscribedAt)}` : ''}
          </span>
        )}
        {manage && (
          <span style={{ marginLeft: 'auto' }}>
            <Confirm
              open={manage.confirming}
              title={`Take ${sub.email} off every list?`}
              detail="No more letters or study updates, as if they had used their own leave-the-list link. Signing up on the site again puts them back."
              confirmLabel="Take off the lists"
              align="right"
              onConfirm={manage.onConfirm}
              onCancel={manage.onCancel}
            >
              <SmallButton onClick={manage.onAsk} disabled={manage.busy}>
                {manage.busy ? 'Removing…' : 'Unsubscribe'}
              </SmallButton>
            </Confirm>
          </span>
        )}
      </div>
    </div>
  );
}

/** One numbered step of the composer. */
function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: color.navy,
            color: color.white,
            fontSize: 11.5,
            fontWeight: 800,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          {n}
        </span>
        <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 800, color: color.navy, letterSpacing: '0.01em' }}>
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

/** The template cards. Choosing one fills the subject and message; the list it suits is only a hint. */
function TemplatePicker({ onPick }: { onPick: (t: LetterTemplate) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 8 }}>
      {LETTER_TEMPLATES.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onPick(t)}
          className="hov-line"
          style={{
            textAlign: 'left',
            border: `1px solid ${color.line}`,
            background: color.white,
            borderRadius: 12,
            padding: '11px 12px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            font: 'inherit',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 800, color: color.navy }}>{t.name}</span>
          <span style={{ fontSize: 11.5, lineHeight: '16px', color: color.muted, fontWeight: 500 }}>{t.use}</span>
          {t.suits && (
            <span style={{ fontSize: 10.5, fontWeight: 700, color: color.blue, marginTop: 2 }}>
              Usually to: {AUDIENCE_NAME[t.suits]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

type Mark = 'heading' | 'list' | 'bold' | 'link' | 'button' | 'name';

const MARKS: Array<{ mark: Mark; label: string; title: string }> = [
  { mark: 'heading', label: 'Heading', title: 'Make this line a section heading (## )' },
  { mark: 'list', label: '• List', title: 'Make these lines a list (- )' },
  { mark: 'bold', label: 'Bold', title: 'Bold the selected words (**words**)' },
  { mark: 'link', label: 'Link', title: 'Link the selected words ([words](https://…))' },
  { mark: 'button', label: 'Button', title: 'A button on its own line ([Label](https://…))' },
  { mark: 'name', label: '{first name}', title: 'The reader’s first name, or nothing when they gave none' },
];

/**
 * The message box, with a bar that writes the formatting marks for you, so
 * nobody has to remember them. What the marks do is visible in the preview.
 */
function LetterEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const box = useRef<HTMLTextAreaElement>(null);

  function apply(mark: Mark) {
    const el = box.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const picked = value.slice(a, b);
    let next = value;
    let from = a;
    let to = b;
    const lineStart = value.lastIndexOf('\n', a - 1) + 1;

    if (mark === 'heading') {
      if (!value.startsWith('## ', lineStart)) {
        next = `${value.slice(0, lineStart)}## ${value.slice(lineStart)}`;
        from = a + 3;
        to = b + 3;
      }
    } else if (mark === 'list') {
      const end = value.indexOf('\n', b) === -1 ? value.length : value.indexOf('\n', b);
      const lines = value.slice(lineStart, end).split('\n').map((l) => (l.startsWith('- ') ? l : `- ${l}`));
      const block = lines.join('\n');
      next = `${value.slice(0, lineStart)}${block}${value.slice(end)}`;
      from = lineStart;
      to = lineStart + block.length;
    } else if (mark === 'bold') {
      const words = picked || 'bold words';
      next = `${value.slice(0, a)}**${words}**${value.slice(b)}`;
      from = a + 2;
      to = from + words.length;
    } else if (mark === 'link') {
      const words = picked || 'link text';
      next = `${value.slice(0, a)}[${words}](https://)${value.slice(b)}`;
      from = a + words.length + 3;
      to = from + 8;
    } else if (mark === 'button') {
      const words = picked || 'Button text';
      const before = value.slice(0, a).replace(/\s*$/, '');
      const insert = `${before ? '\n\n' : ''}[${words}](https://)\n\n`;
      next = `${before}${insert}${value.slice(b).replace(/^\s*/, '')}`;
      from = before.length + (before ? 2 : 0) + words.length + 3;
      to = from + 8;
    } else {
      next = `${value.slice(0, a)}{first name}${value.slice(b)}`;
      from = to = a + 12;
    }

    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(from, to);
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted }}>Message</span>
      <div
        style={{
          border: `1px solid ${color.line}`,
          borderRadius: 12,
          background: color.white,
          overflow: 'hidden',
        }}
      >
        <div
          role="toolbar"
          aria-label="Formatting"
          style={{
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
            padding: '6px 8px',
            borderBottom: `1px solid ${color.line}`,
            background: color.bg,
          }}
        >
          {MARKS.map((m) => (
            <button
              key={m.mark}
              type="button"
              title={m.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => apply(m.mark)}
              className="hov-line"
              style={{
                border: `1px solid ${color.line}`,
                background: color.white,
                borderRadius: 8,
                padding: '5px 10px',
                fontSize: 12,
                fontWeight: m.mark === 'bold' ? 800 : 700,
                color: color.ink,
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
        <textarea
          ref={box}
          value={value}
          rows={16}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Pick a template above, or start writing. A blank line starts a new paragraph."
          aria-label="Message"
          style={{
            ...inputStyle,
            border: 'none',
            borderRadius: 0,
            fontSize: 14,
            lineHeight: '23px',
            padding: '12px 14px',
            resize: 'vertical',
            fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
            display: 'block',
          }}
        />
      </div>
      <span style={{ fontSize: 11.5, color: color.muted, fontWeight: 500, lineHeight: '17px' }}>
        A blank line between paragraphs. Square-bracket prompts like <code>[Study name]</code> must be filled in
        before the letter can go.
      </span>
    </div>
  );
}

/**
 * The email in a frame, sized to its content — and re-sized as its web fonts
 * and logo arrive, which change its height after it first loads. It runs no
 * scripts; its links open in a new tab.
 */
function LetterFrame({ html, width }: { html: string; width: number | '100%' }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(560);
  const watcher = useRef<ResizeObserver | null>(null);
  useEffect(() => () => watcher.current?.disconnect(), []);
  const fit = () => {
    const doc = frame.current?.contentDocument;
    if (!doc) return;
    const measure = () => setHeight(Math.max(320, doc.documentElement.scrollHeight));
    measure();
    watcher.current?.disconnect();
    watcher.current = new ResizeObserver(measure);
    watcher.current.observe(doc.body);
  };
  return (
    <iframe
      ref={frame}
      title="Letter preview"
      srcDoc={html.replace('<head>', '<head><base target="_blank">')}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={fit}
      style={{ width, height, border: 0, display: 'block', margin: '0 auto', background: '#eef4fa' }}
    />
  );
}

/** The first words an inbox shows beside the subject: the letter's opening, after the greeting. */
function snippetOf(text: string): string {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const opening = /^(hello|dear|hi)\b/i.test(lines[0] ?? '') ? lines.slice(1) : lines;
  return opening.join(' ').slice(0, 110);
}

/** The preview beside the composer: an inbox row, then the email, on a desktop or a phone. */
function LetterPreview({
  preview,
  updating,
  error,
}: {
  preview: NewsletterPreview | undefined;
  updating: boolean;
  error: Error | null;
}) {
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop');
  const fromName = preview?.from.replace(/\s*<.*$/, '') || 'ENACT';
  const fromAddress = preview?.from.match(/<([^>]+)>/)?.[1] ?? preview?.from ?? '';

  return (
    <Card pad="18px 18px 20px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <CardTitle style={{ flex: 1 }}>Preview</CardTitle>
        <span style={{ fontSize: 11.5, color: color.muted, fontWeight: 600, minWidth: 90, textAlign: 'right' }}>
          {preview ? (updating ? 'Updating…' : 'As readers will see it') : ''}
        </span>
        <div
          role="group"
          aria-label="Preview size"
          style={{ display: 'inline-flex', background: color.bg, borderRadius: 999, padding: 3, gap: 2 }}
        >
          {(['desktop', 'phone'] as const).map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={device === d}
              onClick={() => setDevice(d)}
              style={{
                border: 'none',
                borderRadius: 999,
                padding: '5px 12px',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                background: device === d ? color.white : 'transparent',
                color: device === d ? color.navy : color.muted,
                boxShadow: device === d ? '0 1px 3px rgba(18,38,63,0.12)' : 'none',
                font: 'inherit',
              }}
            >
              {d === 'desktop' ? 'Desktop' : 'Phone'}
            </button>
          ))}
        </div>
      </div>

      {error && <ErrorNote message={error.message} />}

      {!preview ? (
        <div
          style={{
            border: `1.5px dashed ${color.line}`,
            borderRadius: 14,
            padding: '56px 24px',
            textAlign: 'center',
            color: color.muted,
            fontSize: 13,
            lineHeight: '20px',
          }}
        >
          Pick a template or start writing.
          <br />
          Your letter appears here exactly as readers will see it.
        </div>
      ) : (
        <>
          {/* How it sits in an inbox: who it is from, the subject, the opening words. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '36px minmax(0,1fr)',
              gap: 10,
              alignItems: 'center',
              border: `1px solid ${color.line}`,
              borderRadius: 12,
              padding: '10px 12px',
              marginBottom: 12,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: color.blue,
                color: color.white,
                fontWeight: 800,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {fromName.slice(0, 1)}
            </span>
            <div style={{ minWidth: 0, fontSize: 12.5, lineHeight: '18px' }}>
              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <strong style={{ color: color.navy }}>{fromName}</strong>{' '}
                <span style={{ color: color.muted }}>&lt;{fromAddress}&gt;</span>
              </div>
              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: color.ink }}>
                <strong>{preview.subject}</strong>{' '}
                <span style={{ color: color.muted }}>— {snippetOf(preview.text)}</span>
              </div>
            </div>
          </div>

          <div
            style={{
              background: '#eef4fa',
              borderRadius: device === 'phone' ? 28 : 14,
              padding: device === 'phone' ? '14px 0' : 0,
              width: device === 'phone' ? 403 : '100%',
              maxWidth: '100%',
              margin: '0 auto',
              boxShadow: device === 'phone' ? `0 0 0 10px ${color.navy}, 0 18px 40px rgba(18,38,63,0.25)` : 'none',
              overflow: 'hidden',
              opacity: updating ? 0.75 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            <LetterFrame key={device} html={preview.html} width={device === 'phone' ? 375 : '100%'} />
          </div>

          {preview.unfilled && (
            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: '#5a3d12', fontWeight: 700 }}>
              Still to fill in: {preview.unfilled}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

/** A sent letter, shown again as it went out (rendered with your own first name and a sample leave-the-list link). */
function SentLetterView({ letter }: { letter: LetterDraft }) {
  const preview = useLetterPreview(letter.body.trim() ? letter : null);
  if (preview.isLoading) return <Loading />;
  if (preview.error) return <ErrorNote message={(preview.error as Error).message} />;
  if (!preview.data) return null;
  return <LetterFrame html={preview.data.html} width="100%" />;
}

function StatusChip({ status }: { status: string }) {
  const tone: Record<string, CSSProperties> = {
    sent: { background: '#cfe9d8', color: '#14563c' },
    failed: { background: '#f6dcd5', color: color.red },
    sending: { background: '#d9e7f5', color: color.blueDeep },
    queued: { background: '#e6eaef', color: color.muted },
  };
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 800,
        padding: '3px 10px',
        borderRadius: 999,
        textTransform: 'capitalize',
        ...(tone[status] ?? tone.queued),
      }}
    >
      {status}
    </span>
  );
}

function SmallButton({
  onClick,
  children,
  disabled,
}: {
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={disabled ? undefined : 'hov-line'}
      style={{
        border: `1px solid ${color.line}`,
        background: color.white,
        borderRadius: 999,
        padding: '4px 12px',
        fontSize: 12,
        fontWeight: 700,
        color: color.blueDeep,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        font: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

/** One audience: a radio, the list's name, who is on it, and how many. */
function AudienceChoice({
  on,
  onPick,
  name,
  desc,
  count,
  countShown = true,
  children,
}: {
  on: boolean;
  onPick: () => void;
  name: string;
  desc: string;
  count: number | null;
  countShown?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      style={{
        border: `1px solid ${on ? color.blue : color.line}`,
        background: on ? color.tint : color.white,
        borderRadius: 10,
        padding: '10px 12px',
      }}
    >
      <label
        style={{
          display: 'grid',
          gridTemplateColumns: '16px minmax(0,1fr) auto',
          gap: 10,
          alignItems: 'start',
          cursor: 'pointer',
        }}
      >
        <input
          type="radio"
          name="newsletter-audience"
          checked={on}
          onChange={onPick}
          style={{ accentColor: color.blueDeep, margin: '2px 0 0' }}
        />
        <span>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: color.navy }}>{name}</span>
          <span
            style={{ display: 'block', fontSize: 12, lineHeight: '17px', color: color.muted, fontWeight: 500, marginTop: 2 }}
          >
            {desc}
          </span>
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: color.navy,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {countShown ? (count === null ? '…' : num(count)) : ''}
          {countShown && count !== null && (
            <span style={{ fontWeight: 600, color: color.muted, fontSize: 11.5 }}> {people(count)}</span>
          )}
        </span>
      </label>
      {children}
    </div>
  );
}

/**
 * The studies that have a list: every study someone follows, plus every
 * study still in development (the ones whose page offers the sign-up). A
 * study that has opened keeps its list — its subscribers are exactly who to
 * tell. A study nobody follows yet cannot be ticked.
 */
function StudyPicker({
  studies,
  picked,
  onToggle,
  onAll,
  onClear,
  allTicked,
}: {
  studies: AudienceStudy[];
  picked: string[];
  onToggle: (slug: string) => void;
  onAll: () => void;
  onClear: () => void;
  allTicked: boolean;
}) {
  if (studies.length === 0) {
    return (
      <p style={{ fontSize: 12.5, color: color.muted, margin: '10px 0 0 26px' }}>
        No study has subscribers yet. The sign-up box appears on a study page while the study is in development.
      </p>
    );
  }
  return (
    <div style={{ paddingLeft: 26, paddingTop: 10 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="ghost" onClick={onAll} disabled={allTicked} style={{ padding: '6px 14px', fontSize: 12.5 }}>
          Select all
        </Button>
        <Button
          variant="ghost"
          onClick={onClear}
          disabled={picked.length === 0}
          style={{ padding: '6px 14px', fontSize: 12.5 }}
        >
          Clear
        </Button>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '6px 14px',
          marginTop: 10,
        }}
      >
        {studies.map((s) => {
          const none = s.count === 0;
          const status = studyStatusTheme[s.status];
          return (
            <label
              key={s.slug}
              title={s.label}
              style={{
                display: 'grid',
                gridTemplateColumns: '18px minmax(0,1fr) auto',
                gap: 10,
                alignItems: 'center',
                fontSize: 13,
                fontWeight: 700,
                color: none ? color.muted : color.ink,
                padding: '4px 2px',
                cursor: none ? 'default' : 'pointer',
                minWidth: 0,
              }}
            >
              <input
                type="checkbox"
                checked={picked.includes(s.slug)}
                disabled={none}
                onChange={() => onToggle(s.slug)}
                style={{ accentColor: color.blueDeep, margin: 0 }}
              />
              <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.short}</span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: 999,
                    background: status.bg,
                    color: status.fg,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {status.label}
                </span>
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted, fontVariantNumeric: 'tabular-nums' }}>
                {none ? 'No one yet' : num(s.count)}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function StatRow({ name, title, count }: { name: string; title?: string; count: number }) {
  return (
    <div title={title} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, opacity: 0.94 }}>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      <span>{num(count)}</span>
    </div>
  );
}

/** Which list a sign-up joined: ink on white, because nothing here needs attention. */
function ListTag({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: '3px 9px',
        borderRadius: 999,
        boxShadow: `inset 0 0 0 1px ${color.line}`,
        color: color.ink,
        background: color.white,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}
